<template>
  <div class="space-y-3">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h4 class="text-sm font-medium text-slate-200">Notification History</h4>
      <button
        v-if="entries.length > 0"
        @click="loadEntries"
        class="text-[11px] text-primary-400 hover:text-primary-300 transition-colors"
      >
        Refresh
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center gap-2 py-4">
      <div class="w-4 h-4 border-2 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
      <span class="text-xs text-slate-400">Loading notifications…</span>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="entries.length === 0"
      class="py-6 text-center"
    >
      <svg class="w-8 h-8 mx-auto text-slate-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <p class="text-xs text-slate-500">No notifications sent yet</p>
    </div>

    <!-- Timeline entries -->
    <div v-else class="space-y-1.5 max-h-[400px] overflow-y-auto pr-1">
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="flex items-start gap-3 p-2.5 rounded-lg bg-slate-800/30 border border-slate-700/40 hover:bg-slate-800/50 transition-colors"
      >
        <!-- Channel icon -->
        <div
          :class="[
            'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
            entry.channel === 'email'
              ? 'bg-blue-500/20'
              : 'bg-green-500/20',
          ]"
        >
          <!-- Email icon -->
          <svg v-if="entry.channel === 'email'" class="w-3.5 h-3.5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <!-- SMS icon -->
          <svg v-else class="w-3.5 h-3.5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>

        <!-- Content -->
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-0.5">
            <span class="text-xs font-medium text-white">{{ alertLabel(entry.alert_key) }}</span>
            <span
              :class="[
                'inline-block w-1.5 h-1.5 rounded-full',
                statusColor(entry.status),
              ]"
            ></span>
            <span class="text-[10px] text-slate-500">{{ statusLabel(entry.status) }}</span>
          </div>
          <div class="flex items-center gap-2 text-[11px] text-slate-400">
            <span class="truncate">{{ entry.recipient_name || entry.recipient_email || entry.recipient_phone || '—' }}</span>
            <span class="text-slate-600">·</span>
            <span>{{ formatTime(entry.sent_at || entry.created_at) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { getNotificationLog, type NotificationLogEntry } from '@/lib/alertService'

const props = defineProps<{
  checkoutId: number
}>()

const entries = ref<NotificationLogEntry[]>([])
const loading = ref(false)

const ALERT_LABELS: Record<string, string> = {
  // Outbound lifecycle
  carrier_accepted: 'Carrier Accepted',
  in_transit: 'In Transit',
  out_for_delivery: 'Out for Delivery',
  delivered_to_borrower: 'Delivered to Consignee',
  // Return lifecycle
  return_upcoming: 'Return Due Soon',
  return_overdue: 'Return Overdue',
  return_carrier_scan: 'Return Carrier Accepted',
  return_in_transit: 'Return In Transit',
  returned_to_warehouse: 'Received at Warehouse',
  // Other
  borrower_replied: 'Reply Received',
  owner_replied: 'Reply Sent',
  checked_in: 'Checked In',
  // Legacy
  shipped: 'Equipment Shipped',
}

function alertLabel(key: string): string {
  return ALERT_LABELS[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function statusColor(status: string): string {
  switch (status) {
    case 'sent':
    case 'delivered':
      return 'bg-green-400'
    case 'pending':
      return 'bg-amber-400'
    case 'failed':
      return 'bg-red-400'
    default:
      return 'bg-slate-400'
  }
}

function statusLabel(status: string): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 7) return `${diffDay}d ago`
    return d.toLocaleDateString()
  } catch {
    return iso
  }
}

async function loadEntries() {
  loading.value = true
  try {
    entries.value = await getNotificationLog(props.checkoutId)
  } catch {
    // Error logged in service
  } finally {
    loading.value = false
  }
}

onMounted(loadEntries)
watch(() => props.checkoutId, loadEntries)
</script>
